import { useState } from "react";
import type { ReactNode } from "react";
import { Icon } from "@core/icons";
import "./access.css";

// Adaptaciones de las primitivas del validador
// (`context/prototype/source/validador.jsx:67-122`) a CSS de STD-003 vía
// `access.css`, no vía objetos de estilo inline: `portal/access/
// primitives.tsx` resuelve el mismo prototipo con `style={{...}}` porque el
// portal es de escritorio y no le pesa; el validador es mobile-first para un
// navegador de gama baja (rol web-validator), así que aquí se prefiere una
// sola hoja de reglas reutilizada por clase en vez de repetir el árbol de
// estilos en cada componente. No son las primitivas del portal — no se
// importa nada de `web/src/portal/**`.
//
// A diferencia del portal, acá no hay un componente `PasswordField`
// separado que delega en `Field`: un solo `Field` con `kind="password"`
// resuelve el alternador "Ver"/"Ocultar" internamente (RF-VAL-02, estado
// inicial oculto), porque las tres pantallas de esta superficie no
// necesitan distinguir los dos casos como tipos de componente distintos.
type FieldKind = "text" | "email" | "password";

function fieldClass(hasToggle: boolean, error: boolean | undefined): string {
  const parts = ["sc-input"];
  if (hasToggle) parts.push("sc-input--right");
  if (error) parts.push("sc-input--error");
  return parts.join(" ");
}

export function Field({
  label,
  kind = "text",
  value,
  onChange,
  placeholder,
  error,
  autoComplete,
}: {
  label: string;
  kind?: FieldKind;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: boolean;
  autoComplete?: string;
}) {
  const [passwordVisible, setPasswordVisible] = useState(false);
  const isPassword = kind === "password";
  const inputType =
    isPassword && !passwordVisible ? "password" : isPassword ? "text" : kind;

  return (
    <label className="sc-field">
      <div className="sc-field-label">{label}</div>
      <div className="sc-field-wrap">
        <input
          type={inputType}
          value={value}
          autoComplete={autoComplete}
          onChange={(event) => {
            onChange(event.target.value);
          }}
          placeholder={placeholder}
          className={fieldClass(isPassword, error)}
        />
        {isPassword ? (
          <button
            type="button"
            className="sc-field-toggle"
            onClick={() => {
              setPasswordVisible((visible) => !visible);
            }}
          >
            {passwordVisible ? "Ocultar" : "Ver"}
          </button>
        ) : null}
      </div>
    </label>
  );
}

export function Notice({
  tone = "danger",
  children,
}: {
  tone?: "danger" | "info";
  children: ReactNode;
}) {
  const iconName = tone === "danger" ? "alert" : "check";
  const iconColor = tone === "danger" ? "var(--danger)" : "var(--gold-2)";
  return (
    <div role="alert" className={`sc-notice sc-notice--${tone}`}>
      <Icon
        name={iconName}
        size={19}
        color={iconColor}
        stroke={1.8}
        style={{ flex: "none", marginTop: 1 }}
      />
      <div className="sc-notice-text">{children}</div>
    </div>
  );
}

export function BigButton({
  children,
  onClick,
  type = "button",
  tone = "primary",
  disabled,
  className,
}: {
  children: ReactNode;
  onClick?: () => void;
  type?: "button" | "submit";
  tone?: "primary" | "green" | "ghost";
  disabled?: boolean;
  className?: string;
}) {
  const classes = ["sc-btn", `sc-btn--${tone}`];
  if (className) classes.push(className);
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={classes.join(" ")}
    >
      {children}
    </button>
  );
}

export function TextButton({
  children,
  onClick,
  className,
}: {
  children: ReactNode;
  onClick: () => void;
  className?: string;
}) {
  const classes = ["sc-text-btn"];
  if (className) classes.push(className);
  return (
    <button type="button" onClick={onClick} className={classes.join(" ")}>
      {children}
    </button>
  );
}

// Checkbox accesible para "Mantener la sesión iniciada" (RF-VAL-03). El
// prototipo lo dibuja como un botón con un ícono (`validador.jsx:140-145`);
// acá se expone además el rol/estado ARIA que el prototipo no necesita
// declarar porque no se prueba con lector de pantalla.
export function CheckToggle({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  children: ReactNode;
}) {
  const boxClass = checked
    ? "sc-toggle-box sc-toggle-box--checked"
    : "sc-toggle-box";
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      className="sc-toggle"
      onClick={() => {
        onChange(!checked);
      }}
    >
      <span className={boxClass}>
        {checked ? (
          <Icon name="check" size={15} color="#13201A" stroke={2.6} />
        ) : null}
      </span>
      <span className="sc-toggle-label">{children}</span>
    </button>
  );
}
